import { defineStore } from "pinia"

export const useTemplatesStore = defineStore("templates", {
  state: () => ({
    templates: [],
    isLoading: false,
    error: null,
  }),

  getters: {
    favoriteTemplates: (state) => state.templates.filter((t) => t.is_favorite),

    recentTemplates: (state) => {
      return [...state.templates].sort((a, b) => new Date(b.last_used_at || b.created_at) - new Date(a.last_used_at || a.created_at)).slice(0, 5)
    },

    mostUsedTemplates: (state) => {
      return [...state.templates].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0)).slice(0, 5)
    },
  },

  actions: {
    /**
     * Fetch all templates
     */
    async fetchTemplates() {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/shipment/templates")

        this.templates = response.data.data

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch templates"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Create new template
     */
    async createTemplate(templateData) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/shipment/templates", templateData)

        this.templates.unshift(response.data.data)

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create template"
        return { success: false, error: this.error, errors: error.response?.data?.errors }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update template
     */
    async updateTemplate(templateId, updates) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/shipment/templates/${templateId}`, updates)

        const index = this.templates.findIndex((t) => t.id === templateId)
        if (index !== -1) {
          this.templates[index] = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update template"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Delete template
     */
    async deleteTemplate(templateId) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.delete(`/api/v1/shipment/templates/${templateId}`)

        this.templates = this.templates.filter((t) => t.id !== templateId)

        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to delete template"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Use template (increment usage count)
     */
    async useTemplate(templateId) {
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/templates/${templateId}/use`)

        const index = this.templates.findIndex((t) => t.id === templateId)
        if (index !== -1) {
          this.templates[index] = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        return { success: false, error: error.response?.data?.message }
      }
    },

    /**
     * Get template by ID
     */
    getTemplateById(templateId) {
      return this.templates.find((t) => t.id === templateId)
    },

    /**
     * Clear error
     */
    clearError() {
      this.error = null
    },

    /**
     * Reset store
     */
    reset() {
      this.templates = []
      this.isLoading = false
      this.error = null
    },
  },
})
